import React, { useState, useEffect } from "react";
import api, { extractApiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Sparkles, Settings2 } from "lucide-react";



// ---------------------------------------------------------------------------
// Iteration 35 — Launch-banner remote control (visual-only billboard)
// ---------------------------------------------------------------------------
const AdminBannerSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [bannerText, setBannerText] = useState("");
  const [displayedCode, setDisplayedCode] = useState("");

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/launch-banner");
      setIsActive(!!data?.is_active);
      setBannerText(data?.banner_text || "");
      setDisplayedCode(data?.displayed_code || "");
    } catch {
      toast.error("تعذّر تحميل إعدادات البانر");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/launch-banner", {
        is_active: isActive,
        banner_text: bannerText.trim(),
        displayed_code: displayedCode.trim(),
      });
      toast.success("تم حفظ إعدادات البانر");
    } catch (e) {
      toast.error(extractApiErrorMessage(e) || "تعذّر الحفظ");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-banner-settings" className="space-y-6" dir="rtl">
      <div className="card-warm p-5">
        <div className="flex items-center gap-2 mb-1">
          <Settings2 className="w-5 h-5 text-[#0A4D3C]" />
          <h3 className="font-heading text-xl text-[#222222]">إعدادات البانر</h3>
        </div>
        <p className="text-sm text-[#5E5C58] leading-relaxed">
          هذا البانر مجرّد لوحة إعلانية في أعلى الموقع. الكود الذي يَظهر هنا هو
          نصّ معروض فقط — للتفعيل الفعلي للخصم استخدم تبويب
          <strong className="mx-1">«أكواد الخصم»</strong>.
        </p>

        <div className="mt-5 flex items-center justify-between bg-[#F6F4EE] border border-[#E5E1D8] rounded-2xl p-4">
          <div>
            <div className="text-sm font-medium text-[#222222]">تفعيل البانر الإعلاني</div>
            <div className="text-xs text-[#5E5C58] mt-0.5">
              عند الإيقاف، يُخفى البانر عن جميع الزوار فورًا.
            </div>
          </div>
          <Switch
            data-testid="banner-active-switch"
            checked={isActive}
            onCheckedChange={setIsActive}
            disabled={loading}
          />
        </div>

        <div className="mt-4 grid gap-4">
          <div>
            <label htmlFor="banner-text" className="block text-xs text-[#5E5C58] mb-1">
              النص الإعلاني
            </label>
            <Input
              id="banner-text"
              data-testid="banner-text-input"
              value={bannerText}
              onChange={(e) => setBannerText(e.target.value)}
              maxLength={300}
              placeholder="مثال: عرض بمناسبة التدشين!"
              disabled={loading}
            />
            <div className="mt-1 text-[11px] text-[#8B8780]">{bannerText.length}/300</div>
          </div>
          <div>
            <label htmlFor="banner-code" className="block text-xs text-[#5E5C58] mb-1">
              كود الخصم المعروض (نصّ للعرض فقط)
            </label>
            <Input
              id="banner-code"
              data-testid="banner-code-input"
              value={displayedCode}
              onChange={(e) => setDisplayedCode(e.target.value)}
              maxLength={60}
              placeholder="مثال: SAUDI94"
              dir="ltr"
              className="font-mono"
              disabled={loading}
            />
            <div className="mt-1 text-[11px] text-[#8B8780]">
              هذا الكود لا يُفعّل أي خصم — فقط يَظهر على البانر. أنشئ الكوبون الفعلي
              من تبويب «أكواد الخصم».
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <Button
            type="button"
            data-testid="banner-save-btn"
            onClick={save}
            disabled={saving || loading}
            className="rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white"
          >
            {saving ? "جاري الحفظ..." : "حفظ"}
          </Button>
        </div>
      </div>

      {(isActive && (bannerText.trim() || displayedCode.trim())) && (
        <div data-testid="banner-preview" className="card-warm p-0 overflow-hidden">
          <div className="px-4 py-2 bg-[#F6F4EE] border-b border-[#E5E1D8] text-xs text-[#5E5C58]">
            معاينة البانر
          </div>
          <div className="bg-[#0A4D3C] text-[#FAF9F6]">
            <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center gap-3 text-sm">
              <Sparkles className="w-4 h-4 text-[#C5A059] shrink-0" />
              <span className="truncate">
                <strong className="text-[#C5A059] font-semibold">
                  {bannerText.trim() || "—"}
                </strong>
              </span>
              {displayedCode.trim() && (
                <span className="ms-2 inline-flex items-center rounded-full bg-[#C5A059] text-[#222222] text-[11px] font-bold px-2.5 py-0.5 font-mono tracking-widest" dir="ltr">
                  {displayedCode.trim()}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { AdminBannerSettings };
